type RecordingErrorCategory =
  | "auth"
  | "permission"
  | "not_found"
  | "invalid_request"
  | "storage"
  | "rate_limited"
  | "network"
  | "server"
  | "unknown";

type SerializedRecordingError = {
  message: string;
  name: string;
  stack?: string;
  category: RecordingErrorCategory;
  isRetriable: boolean;
  httpStatus?: number;
  firebaseStatus?: string;
  functionName?: string;
};

type RecordingErrorClassification = {
  category: RecordingErrorCategory;
  isRetriable: boolean;
};

// Fatal categories abort the recording (nothing we can do without user/env
// intervention). Everything else is safe to keep retrying.
const FATAL_CATEGORIES: ReadonlySet<RecordingErrorCategory> = new Set([
  "auth",
  "permission",
  "not_found",
  "invalid_request",
  "storage",
]);

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "Unknown error";
};

const getErrorName = (error: unknown): string => {
  if (error instanceof Error) {
    return error.name;
  }
  return "Error";
};

const getErrorStack = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.stack;
  }
  return undefined;
};

const getNumericProperty = (
  error: unknown,
  key: string
): number | undefined => {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const value = Reflect.get(error, key);
  return typeof value === "number" ? value : undefined;
};

const getStringProperty = (error: unknown, key: string): string | undefined => {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const value = Reflect.get(error, key);
  return typeof value === "string" ? value : undefined;
};

const isIndexedDbQuotaError = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === "QuotaExceededError" || error.code === 22);

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TypeError) {
    return true;
  }

  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("load failed")
  );
};

const isAuthMessage = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("no hybrid token") ||
    normalized.includes("unauthenticated") ||
    normalized.includes("permission denied") ||
    normalized.includes("firebase authentication") ||
    normalized.includes("sign in with firebase")
  );
};

const toClassification = (
  category: RecordingErrorCategory
): RecordingErrorClassification => ({
  category,
  isRetriable: !FATAL_CATEGORIES.has(category),
});

// Firebase callable "status" strings map cleanly onto our categories. This is
// the most reliable signal we have, so it takes precedence over HTTP status and
// message heuristics.
const categoryForFirebaseStatus = (
  status: string
): RecordingErrorCategory | undefined => {
  switch (status) {
    case "UNAUTHENTICATED":
    case "PERMISSION_DENIED":
      return "auth";
    case "NOT_FOUND":
      return "not_found";
    case "FAILED_PRECONDITION":
    case "INVALID_ARGUMENT":
    case "OUT_OF_RANGE":
    case "ALREADY_EXISTS":
      return "invalid_request";
    case "RESOURCE_EXHAUSTED":
      return "rate_limited";
    case "UNAVAILABLE":
    case "DEADLINE_EXCEEDED":
    case "INTERNAL":
    case "ABORTED":
      return "server";
    default:
      return undefined;
  }
};

const categoryForHttpStatus = (
  status: number
): RecordingErrorCategory | undefined => {
  if (status === 401 || status === 403) {
    return "auth";
  }
  if (status === 404) {
    return "not_found";
  }
  if (status === 408) {
    return "network";
  }
  if (status === 429) {
    return "rate_limited";
  }
  if (status >= 500) {
    return "server";
  }
  if (status >= 400) {
    return "invalid_request";
  }
  return undefined;
};

const classifyRecordingError = (
  error: unknown
): RecordingErrorClassification => {
  // Browser IndexedDB quota: local storage for buffered chunks is full.
  if (isIndexedDbQuotaError(error)) {
    return toClassification("storage");
  }

  // Microphone permission was denied / blocked.
  if (
    error instanceof DOMException &&
    (error.name === "NotAllowedError" || error.name === "SecurityError")
  ) {
    return toClassification("permission");
  }

  const firebaseStatus = getStringProperty(error, "firebaseStatus");
  if (firebaseStatus) {
    const category = categoryForFirebaseStatus(firebaseStatus);
    if (category) {
      return toClassification(category);
    }
  }

  const httpStatus = getNumericProperty(error, "httpStatus");
  if (httpStatus !== undefined) {
    const category = categoryForHttpStatus(httpStatus);
    if (category) {
      return toClassification(category);
    }
  }

  // Fallback heuristics for errors that carry no structured status.
  const message = getErrorMessage(error);
  if (isAuthMessage(message)) {
    return toClassification("auth");
  }

  if (message.includes("Recording session has not been started")) {
    return toClassification("invalid_request");
  }

  if (message.toLowerCase().includes("indexeddb")) {
    return toClassification("storage");
  }

  if (isNetworkError(error)) {
    return toClassification("network");
  }

  return toClassification("unknown");
};

const serializeRecordingError = (error: unknown): SerializedRecordingError => {
  const { category, isRetriable } = classifyRecordingError(error);

  return {
    message: getErrorMessage(error),
    name: getErrorName(error),
    stack: getErrorStack(error),
    category,
    isRetriable,
    httpStatus: getNumericProperty(error, "httpStatus"),
    firebaseStatus: getStringProperty(error, "firebaseStatus"),
    functionName: getStringProperty(error, "functionName"),
  };
};

const isRetriableUploadError = (error: unknown): boolean =>
  classifyRecordingError(error).isRetriable;

// A "fatal" error means retrying is pointless without user/environment
// intervention, so the recording should be torn down and surfaced to the rep.
// Retriable errors (network blips, rate limiting, transient 5xx) are handled by
// the upload retry/backoff loop instead and must never abort the recording.
const isFatalUploadError = (error: unknown): boolean =>
  FATAL_CATEGORIES.has(classifyRecordingError(error).category);

const getUploadFailureMessage = (error: unknown): string => {
  const { category } = classifyRecordingError(error);

  switch (category) {
    case "auth":
      return "Your session expired. Please refresh the page and start recording again.";
    case "storage":
      return "Your device is out of storage for recordings. Please free up space and try again.";
    case "not_found":
      return "We couldn't find this recording session. Please start a new recording.";
    case "invalid_request":
      return "We couldn't save your recording due to an unexpected issue. Please start a new recording.";
    case "rate_limited":
      return "The recording service is busy right now. Please wait a moment and try again.";
    case "server":
      return "The recording service is temporarily unavailable. Please try again in a moment.";
    case "network":
      return "We lost connection while uploading your recording. Please check your internet and try again.";
    case "permission":
      return "Microphone access is required to continue recording. Please allow microphone access and try again.";
    default:
      return "We couldn't upload your recording. Please try again.";
  }
};

export {
  classifyRecordingError,
  getUploadFailureMessage,
  isFatalUploadError,
  isRetriableUploadError,
  serializeRecordingError,
};
export type {
  RecordingErrorCategory,
  RecordingErrorClassification,
  SerializedRecordingError,
};
